import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';

export type SuiShellSidenavPosition = 'start' | 'end';

/**
 * Application shell: the top bar, the side navigation and the main area on the layout
 * tokens (`--layout-topbar-height`, the sidebar widths are the nav's own). Project a
 * `sui-shell-topbar` and a `sui-shell-sidenav`; everything else projected is the main area.
 * `sidenavPosition` puts the navigation at the start (default) or the end of the body.
 *
 * Under the shell drawer breakpoint (`$breakpoint-shell-drawer`) the side navigation leaves
 * the flow and becomes an off-canvas drawer: `drawerOpen` slides it in over a backdrop, the
 * backdrop click and Escape emit `drawerOpenChange(false)`. The consumer owns the toggle
 * button (usually in the top bar) and the height of the host.
 *
 * A top bar that brings its own chrome sets `--sui-shell-topbar-bg`, `--sui-shell-topbar-fg`
 * and `--sui-shell-topbar-padding` on the host; a navigation column without the divider
 * sets `--sui-shell-sidenav-border-color` (the nav's own colours are `--sui-sidebar-nav-*`).
 */
@Component({
  selector: 'sui-shell',
  template: `
    <div
      class="sui-shell"
      [attr.data-sidenav]="sidenavPosition"
      [class.sui-shell--drawer-open]="drawerOpen"
    >
      <ng-content select="sui-shell-topbar" />
      <div class="sui-shell__body">
        <ng-content select="sui-shell-sidenav" />
        <main class="sui-shell__main"><ng-content /></main>
        <div class="sui-shell__backdrop" (click)="closeDrawer()"></div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './sui-shell.component.scss',
})
export class SuiShellComponent {
  @Input() sidenavPosition: SuiShellSidenavPosition = 'start';
  @Input({ transform: booleanAttribute }) drawerOpen = false;
  @Output() drawerOpenChange = new EventEmitter<boolean>();

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.closeDrawer();
  }

  protected closeDrawer(): void {
    if (this.drawerOpen) {
      this.drawerOpenChange.emit(false);
    }
  }
}

/** Top bar of the shell: brand, primary links and the user cluster, on the topbar height token. */
@Component({
  selector: 'sui-shell-topbar',
  template: `<ng-content />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './sui-shell-topbar.component.scss',
})
export class SuiShellTopbarComponent {}

/** Side navigation column of the shell; usually holds one `sui-sidebar-nav`. */
@Component({
  selector: 'sui-shell-sidenav',
  template: `<ng-content />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './sui-shell-sidenav.component.scss',
})
export class SuiShellSidenavComponent {}
